import type { ReadCustomBlockGitType } from '@readme/api/src/mappings/customblock/types';
import type { $TSFixMe } from '@readme/iso';

import React, { useCallback, useContext, useRef } from 'react';

import type { ProjectContextValue } from '@core/context';
import { ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useEventListener from '@core/hooks/useEventListener';
import useLegacyCompatibleBranchName from '@core/hooks/useLegacyCompatibleBranchName';
import useSentry from '@core/hooks/useSentry';
import { useSuperHubStore, useProjectStore } from '@core/store';

import { ValidationError } from '@routes/SuperHub/Editor/Form/components/MarkdownEditor/ValidationError';

import type { DashEditorNoProxyProps } from '@ui/MarkdownEditor/DashEditor/NoProxy';
import DashEditorNoProxy from '@ui/MarkdownEditor/DashEditor/NoProxy';
import type Modal from '@ui/Modal';
import { RHFGroup } from '@ui/RHF';

import { useCustomBlockFormContext } from '../Context';
import { useCustomBlockEditorContext } from '../EditorContext';

import classes from './style.module.scss';

export interface Props {
  isRawMode: boolean;
  mdxRenderable?: ReadCustomBlockGitType['data']['renderable'];
  name?: string;
}

type EditorWrapperProps = Omit<DashEditorNoProxyProps, 'assetUrl' | 'modal' | 'ref' | 'sentry'> & { field: $TSFixMe };

const EditorWrapper = ({ field, ...props }: EditorWrapperProps) => {
  const sentry = useSentry('reusableContentEditor');
  const { setIsDirty, setEditor } = useCustomBlockEditorContext();

  const modal = useRef<Modal>(null);

  useEventListener('error', sentry);

  const onRawChange = useCallback(
    (value: string) => {
      setIsDirty(true);
      field.onChange(value);
    },
    [field, setIsDirty],
  );

  const onChange = useCallback(() => {
    setIsDirty(true);
  }, [setIsDirty]);

  return (
    <DashEditorNoProxy
      {...props}
      doc={field.value as string}
      editorOnChange={onChange}
      modal={modal.current}
      onInitEditor={setEditor}
      rawOnChange={onRawChange}
    />
  );
};

export default function ReusableContentEditor({ isRawMode, mdxRenderable, name }: Props) {
  const bem = useClassy(classes, 'ReusableContentEditor');

  const {
    project: { fullBaseUrl, childrenProjects, glossaryTerms, subdomain, variableDefaults, _parent, flags },
  } = useContext(ProjectContext) as ProjectContextValue;
  const version = useLegacyCompatibleBranchName();
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const { editor } = useCustomBlockEditorContext();
  const projectId = useProjectStore(s => s.data.id);

  const {
    control,
    formState: { errors },
    getValues,
  } = useCustomBlockFormContext();

  const useMDX = _parent && 'flags' in _parent ? _parent.flags?.mdx : flags?.mdx || false;
  const isGroup = !!childrenProjects?.length;

  const validate = useCallback(() => {
    const value = isRawMode ? getValues('source').trim() : editor?.toString();
    return value === '' ? 'Required' : undefined;
  }, [editor, getValues, isRawMode]);

  return (
    <div className={bem('&', errors?.source && '_error')}>
      <ValidationError
        errorType={mdxRenderable?.status === false ? 'migration' : undefined}
        message={mdxRenderable?.status === false ? mdxRenderable?.error : undefined}
        projectId={projectId}
        projectName={subdomain}
        slug={name}
        title={name}
      />
      <RHFGroup className={bem('-formgroup')} control={control} name="source" validate={validate}>
        {({ field }) => {
          return (
            <EditorWrapper
              data-testid="ReusableContentEditor"
              disallowCustomBlocks
              disallowRecipes={isGroup}
              field={field}
              glossaryTerms={glossaryTerms}
              projectBaseUrl={fullBaseUrl}
              rawMode={isRawMode}
              subdomain={subdomain}
              useAPIv2={isSuperHub}
              useMDX={useMDX}
              variableDefaults={variableDefaults}
              version={version}
            />
          );
        }}
      </RHFGroup>
    </div>
  );
}
